import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
} from '@reduxjs/toolkit';
import type { Filter, FilterCategory } from '@interfaces/filters/catalog';
import {
  normaliseBootstrapFilters,
  type BootstrapFilter,
} from './normaliseBootstrapFilters';

// No sortComparer: the bootstrap sends filters ordered by category display
// sequence, then filter display sequence, then name (both set by admins), and
// `setAll` keeps that order. Categories follow first appearance in that list.
export const categoriesAdapter = createEntityAdapter<FilterCategory>();
export const filtersAdapter = createEntityAdapter<Filter>();

/**
 * Type of the filter EntityState slice (`FiltersState["filters"]`). Kept as a
 * named export because many consumers (payload mappers, FileDownload request
 * builders, `utils/filters`, `utils/segments`) annotate their `filters` param
 * with it. Filter ids remain numeric (unlike segments, which re-keyed to ULID
 * string codes).
 */
export type FiltersAdapter = EntityState<Filter, number>;

export interface FiltersState {
  categories: EntityState<FilterCategory, number>;
  filters: EntityState<Filter, number>;
  initialized: boolean;
}

const initialState: FiltersState = {
  categories: categoriesAdapter.getInitialState(),
  filters: filtersAdapter.getInitialState(),
  initialized: false,
};

const filtersSlice = createSlice({
  name: 'filters',
  initialState,
  reducers: {
    setFiltersCatalog: (
      state,
      action: PayloadAction<BootstrapFilter[] | null>,
    ) => {
      const raw = action.payload ?? [];
      const { filters, categories } = normaliseBootstrapFilters(raw);
      categoriesAdapter.setAll(state.categories, categories);
      filtersAdapter.setAll(state.filters, filters);
      state.initialized = true;
    },
    resetFiltersCatalog: (state) => {
      categoriesAdapter.removeAll(state.categories);
      filtersAdapter.removeAll(state.filters);
      state.initialized = false;
    },
  },
});

export const { setFiltersCatalog, resetFiltersCatalog } = filtersSlice.actions;
export default filtersSlice.reducer;
